import {
  ClientProfile,
  CoachProfile,
  FlexBox,
  JoonUIColor,
  Modal,
  SPACING,
  TextButton,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/react-table"
import dayjs from "dayjs"
import { useMemo, useState } from "react"

import tombstoneIcon from "./tombstone-regular.svg"
import CustomTable from "../../components/table/CustomTable"
import { useDeactivatedCoachingGroupsQuery } from "../../networking/coachingGroups/queries"
import { isJoonCoach } from "../../util/misc"

interface GraveyardTableData {
  coachName: string
  clientName: string
  coachProfile: CoachProfile | undefined
  channelUrl: string
  createdAt: string
  deactivationDate: string
}

const Graveyard = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { data: coachingGroups } = useDeactivatedCoachingGroupsQuery()

  const tableData = useMemo(() => {
    return coachingGroups
      ?.map((group) => {
        const coachProfile = group.deactivated_profiles.find(
          (profile) => profile.role === UserRole.COACH && profile.id !== 3774501
        ) as CoachProfile | undefined
        const clientProfile = group.deactivated_profiles.find(
          (profile) => profile.role === UserRole.CLIENT
        ) as ClientProfile

        // Exclude Joon coaches
        if (coachProfile && isJoonCoach(coachProfile)) {
          return null
        }

        return {
          coachName:
            coachProfile?.user?.name && coachProfile?.user?.last_name
              ? `${coachProfile?.user?.name} ${
                  coachProfile?.user?.last_name || ""
                }`
              : "N/A",
          coachProfile: coachProfile,
          clientName: clientProfile?.user?.name
            ? `${clientProfile?.user?.name} ${
                clientProfile?.user?.last_name || ""
              }`
            : "N/A",
          deactivationDate: dayjs(group.date_deactivated).format("MMM D, YYYY"),
          channelUrl: group.sendbird_channel_url,
          createdAt: dayjs(group.date_created).format("MMM D, YYYY"),
        }
      })
      .filter((data): data is GraveyardTableData => data !== null) // Type guard to filter out null
      .sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))
  }, [coachingGroups])

  const columnHelper = createColumnHelper<GraveyardTableData>()

  const columns = [
    columnHelper.accessor(
      (row) => ({
        name: row.coachName,
        photoUrl: row.coachProfile?.profile_image_url,
      }),
      {
        id: "coachName",
        header: ({ column }) => (
          <button
            onClick={column.getToggleSortingHandler()}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <Typography
              variant="bodySmallBold"
              color={JoonUIColor.text.secondary}
            >
              Coach
            </Typography>
            {column.getIsSorted()
              ? column.getIsSorted() === "asc"
                ? "🔼"
                : "🔽"
              : ""}
          </button>
        ),
        cell: (info) => (
          <FlexBox
            direction="row"
            wrap={false}
            align="center"
            gap={SPACING.space2}
          >
            <img
              src={info.getValue().photoUrl || ""}
              alt="Coach"
              style={{ width: "20px", height: "20px", borderRadius: "50%" }}
            />
            {info.getValue().name || "N/A"}
          </FlexBox>
        ),
        sortingFn: (a, b) => {
          const nameA = a.original.coachName.split(" ")[0].toLowerCase()
          const nameB = b.original.coachName.split(" ")[0].toLowerCase()
          return nameA.localeCompare(nameB)
        },
      }
    ),
    columnHelper.accessor((row) => row.clientName, {
      id: "clientName",
      header: () => (
        <Typography variant="bodySmallBold" color={JoonUIColor.text.secondary}>
          Client
        </Typography>
      ),
      cell: (info) => info.getValue() || "N/A",
    }),
    columnHelper.accessor("deactivationDate", {
      header: ({ column }) => (
        <button
          onClick={column.getToggleSortingHandler()}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.text.secondary}
          >
            Deactivation Date
          </Typography>
          {column.getIsSorted()
            ? column.getIsSorted() === "asc"
              ? "🔼"
              : "🔽"
            : ""}
        </button>
      ),
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      sortingFn: (a, b) =>
        dayjs(a.original.deactivationDate).diff(
          dayjs(b.original.deactivationDate)
        ),
    }),
    columnHelper.accessor("createdAt", {
      header: ({ column }) => (
        <button
          onClick={column.getToggleSortingHandler()}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.text.secondary}
          >
            Date Created
          </Typography>
          {column.getIsSorted()
            ? column.getIsSorted() === "asc"
              ? "🔼"
              : "🔽"
            : ""}
        </button>
      ),
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      sortingFn: (a, b) =>
        dayjs(a.original.createdAt).diff(dayjs(b.original.createdAt)),
    }),
    columnHelper.accessor((row) => row.channelUrl, {
      id: "channelUrl",
      header: () => (
        <Typography variant="bodySmallBold" color={JoonUIColor.text.secondary}>
          Channel URL
        </Typography>
      ),
      cell: (info) => (
        <button
          style={{
            border: `1px solid ${JoonUIColor.border.default}`,
            borderRadius: "4px",
            padding: "4px 8px",
          }}
          onClick={() => handleViewChannel(info.getValue())}
        >
          View Channel
        </button>
      ),
    }),
  ]

  const handleViewChannel = (channelUrl: string) => {
    const baseUrl =
      "https://dashboard.sendbird.com/DD845C5B-E35E-4054-822B-40C9D98819B8/group-channels/"
    window.open(baseUrl + channelUrl, "_blank")
  }

  const table = useReactTable({
    data: tableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  })

  return (
    <>
      <TextButton
        onClick={() => setIsOpen(true)}
        style={{ marginLeft: "auto" }}
      >
        <FlexBox direction="column" align="center" justify="center">
          <img src={tombstoneIcon} alt="Graveyard" style={{ height: "24px" }} />
        </FlexBox>
      </TextButton>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} displayCloseIcon>
        <FlexBox
          direction="column"
          align="center"
          gap={SPACING.space4}
          wrap={false}
          style={{
            padding: SPACING.space6,
            maxHeight: "95vh",
            overflowY: "auto",
            width: "min(90vw, 1200px)",
          }}
        >
          <FlexBox
            direction="row"
            align="center"
            gap={SPACING.space2}
            justify="center"
          >
            <Typography variant="h3">Deactivated coaching groups</Typography>
          </FlexBox>
          <CustomTable table={table} disablePagination />
        </FlexBox>
      </Modal>
    </>
  )
}

export default Graveyard
